import { useEffect, useState, type FormEvent } from 'react';
import type { CreateDataFeedGrantInput, DataFeedGrantIssueResult } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { useCreateDataFeedGrant } from './api.js';

interface DataFeedGrantFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  personName: string;
  email: string;
  expiresAt: string;
}

const EMPTY_FORM: FormState = { personName: '', email: '', expiresAt: '' };

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

/** Creates a per-person Excel/OData credential and reveals the key exactly once. */
export function DataFeedGrantFormModal({
  isOpen,
  onClose,
}: DataFeedGrantFormModalProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [issued, setIssued] = useState<DataFeedGrantIssueResult | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreateDataFeedGrant();
  const resetCreate = createMutation.reset;

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setIssued(null);
      setCopied(false);
      resetCreate();
    }
  }, [isOpen, resetCreate]);

  const fieldErrors = fieldErrorsOf(createMutation.error);
  const generalError =
    createMutation.error instanceof ApiError &&
    !createMutation.error.envelope?.error.details?.length
      ? createMutation.error.message
      : createMutation.isError && !(createMutation.error instanceof ApiError)
        ? 'Failed to grant Excel access.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateDataFeedGrantInput = {
      personName: form.personName.trim(),
      email: form.email.trim() || null,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    };
    createMutation.mutate(payload, { onSuccess: (result) => setIssued(result) });
  };

  const handleCopy = (): void => {
    if (!issued) return;
    void navigator.clipboard.writeText(issued.plaintextKey).then(() => setCopied(true));
  };

  if (issued) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Excel access granted"
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Copy this key now and give it to {issued.personName}. For security it is shown only once
            and cannot be retrieved again.
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              {issued.personName}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-800">
                {issued.plaintextKey}
              </code>
              <Button variant="secondary" size="sm" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            In Excel, choose <strong>Data → Get Data → From Other Sources → From OData Feed</strong>
            , paste the feed URL, and when prompted for credentials choose <strong>Basic</strong>.
            Use any username and paste this key as the password. Excel stores it securely and
            refreshes without signing in to Catalyst PSA.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Grant Excel access"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="data-feed-grant-form" isLoading={createMutation.isPending}>
            Grant access
          </Button>
        </div>
      }
    >
      <form id="data-feed-grant-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {generalError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {generalError}
          </div>
        ) : null}
        <TextField
          label="Person"
          value={form.personName}
          onChange={(e) => setForm((f) => ({ ...f, personName: e.target.value }))}
          error={fieldErrors.personName}
          required
          placeholder="Who is this credential for?"
        />
        <TextField
          label="Email (optional)"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          error={fieldErrors.email}
          placeholder="name@company.com"
        />
        <TextField
          label="Expires (optional)"
          type="date"
          value={form.expiresAt}
          onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
          error={fieldErrors.expiresAt}
        />
      </form>
    </Modal>
  );
}
